import { Injectable, signal } from '@angular/core';
import { fromEvent } from 'rxjs';
import { take } from 'rxjs/operators';

// The native BeforeInstallPromptEvent interface is still experimental and not part of
// the TS DOM lib yet. We declare the minimal shape we need here so we can work
// with a typed object instead of casting to `any` everywhere.
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const LOCAL_STORAGE_SKIP_KEY = 'cf-pwa-skip-install';

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  // --- private reactive state ---------------------------------------------
  #deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);
  #shouldShowPrompt = signal(false);

  // --- public readonly API -------------------------------------------------
  readonly deferredPrompt = this.#deferredPrompt.asReadonly();
  readonly shouldShowPrompt = this.#shouldShowPrompt.asReadonly();

  constructor() {
    const skip = window.localStorage?.getItem(LOCAL_STORAGE_SKIP_KEY) === 'true';
    if (skip) {
      return; // never register listener if the user opted-out
    }

    // Safari on iOS/macOS never dispatches `beforeinstallprompt`; we need to show
    // the instructional modal immediately.
    if (this.isSafariLike()) {
      this.#shouldShowPrompt.set(true);
      return;
    }

    // Chromium path — wait for the install prompt event.
    fromEvent<BeforeInstallPromptEvent>(window, 'beforeinstallprompt')
      .pipe(take(1))
      .subscribe((event) => {
        event.preventDefault();
        this.#deferredPrompt.set(event);
        this.#shouldShowPrompt.set(true);
      });
  }

  /** Opens the native browser install dialog and resolves once the user makes a choice. */
  async promptInstall(): Promise<void> {
    const promptEvent = this.#deferredPrompt();
    if (!promptEvent) return;

    // Show the browser prompt.
    await promptEvent.prompt();

    // Wait for the user's response.
    await promptEvent.userChoice;

    // Whether they accepted or dismissed we hide our UI and reset state.
    this.#shouldShowPrompt.set(false);
    this.#deferredPrompt.set(null);
  }

  /** Permanently hides the prompt for this browser by storing a flag. */
  skipForever(): void {
    window.localStorage.setItem(LOCAL_STORAGE_SKIP_KEY, 'true');
    // Clear state so UI disappears.
    this.#shouldShowPrompt.set(false);
    this.#deferredPrompt.set(null);
  }

  /** Detects iOS Safari and desktop Safari (where beforeinstallprompt is absent). */
  isSafariLike(): boolean {
    const ua = navigator.userAgent.toLowerCase();

    const isiOS = /iphone|ipad|ipod/.test(ua);

    const isSafariDesktop =
      /macintosh|mac os x/.test(ua) &&
      /safari/.test(ua) &&
      !/chrome|crios|chromium|firefox|fxios|opera|opr|edg|edgios/.test(ua);

    return isiOS || isSafariDesktop;
  }
}
